import { IconPlayerPlayFilled } from '@tabler/icons-react'
import classNames from 'classnames'
import { ProductCarouselSlide } from 'shared-types'
import { Image } from '~/components/Image'
import { SlidesCarousel } from '~/components/SlidesCarousel'
import { convertProductImageUrl, getSlideSrcSet } from '~/helpers'
import { usePage } from '~/hooks'
import styles from './GalleryThumbnails.module.scss'

export interface GalleryThumbnailsProps {
  slides: ProductCarouselSlide[]
  selectedIndex: number
  onSelect: (index: number) => void
  imagesOnly?: boolean
  // Lay the strip out in normal flow rather than floating over the media; the
  // Gallery places it after the media box either way.
  belowMedia?: boolean
}

// The button is --pdp-gallery-thumbnail-size square (56px, 40px on mobile) with
// a 2px border, so the image box is 52px or 36px. The candidates match the
// desktop box at 1x and 2x; mobile picks them a step early, and 3x displays
// get the 2x one, which upscales a little at this size without looking soft.
const SRCSET_WIDTHS = [52, 104]
const SIZES = '(max-width: 768px) 36px, 52px'

// The slides the strip shows, paired with their index in the full list. Also
// used by Gallery to know whether the strip renders at all, so the media box
// only reserves room for it when it does.
export const getVisibleThumbnailSlides = (
  slides: ProductCarouselSlide[],
  imagesOnly: boolean
) => {
  return slides
    .map((slide, index) => {
      return { slide, index }
    })
    .filter(({ slide }) => {
      return !imagesOnly || slide.type === 'image'
    })
}

export const hasVisibleThumbnails = (
  slides: ProductCarouselSlide[],
  imagesOnly: boolean
) => {
  return getVisibleThumbnailSlides(slides, imagesOnly).length > 1
}

export const GalleryThumbnails = ({
  slides,
  selectedIndex,
  onSelect,
  imagesOnly = false,
  belowMedia = false,
}: GalleryThumbnailsProps) => {
  const { siteCode } = usePage()
  const visibleSlides = getVisibleThumbnailSlides(slides, imagesOnly)

  if (!hasVisibleThumbnails(slides, imagesOnly)) {
    return null
  }

  // Where the selected slide sits in the strip, so the strip can scroll it
  // into view when the selection changes from outside (the gallery's arrows).
  const activeVisibleIndex = visibleSlides.findIndex(({ index }) => {
    return index === selectedIndex
  })

  return (
    <div
      className={classNames(styles.root, {
        [styles.belowMedia]: belowMedia,
      })}>
      <SlidesCarousel
        tag='ul'
        className={styles.strip}
        activeIndex={activeVisibleIndex === -1 ? undefined : activeVisibleIndex}
        prevLabel='Show previous thumbnails'
        nextLabel='Show more thumbnails'>
        {visibleSlides.map(({ slide, index }, visibleIndex) => {
          const isActive = index === selectedIndex
          const isVideo = slide.type === 'video'
          const imageUrl = convertProductImageUrl(slide.src, siteCode)
          const srcSet = getSlideSrcSet(imageUrl, SRCSET_WIDTHS)

          return (
            <button
              key={index}
              type='button'
              aria-label={
                isVideo
                  ? `Play video ${visibleIndex + 1} of ${visibleSlides.length}`
                  : `Show image ${visibleIndex + 1} of ${visibleSlides.length}`
              }
              aria-current={isActive}
              onClick={() => {
                onSelect(index)
              }}
              className={classNames(styles.button, {
                [styles.active]: isActive,
                [styles.videoButton]: isVideo,
              })}>
              {isVideo ? (
                <div className={styles.playIconContainer}>
                  <IconPlayerPlayFilled size={10} className={styles.playIcon} />
                </div>
              ) : (
                <Image
                  src={imageUrl}
                  alt={slide.altText}
                  width={slide.width}
                  height={slide.height}
                  addSrcSet={false}
                  srcSet={srcSet}
                  sizes={SIZES}
                  className={styles.image}
                />
              )}
            </button>
          )
        })}
      </SlidesCarousel>
    </div>
  )
}
